import { MaintenanceModeService } from './maintenance-mode.service';
import { Injectable, inject } from '@angular/core';
import { HateoasResourceService, Include, Resource } from '@lagoshny/ngx-hateoas-client';
import { lastValueFrom } from 'rxjs';
import { AsyncWorker } from 'src/shared-utilities/async-helpers/async-worker/async-worker';
import { delayAsync } from 'src/shared-utilities/async-helpers/promise-helpers';
import { SaveErrorService, SaveErrorStates } from '../../error/save-error-overlay/save-error.service';
import { Command, CommandBase, CommandStream } from './command-stream';
import { ResourceContext } from './resource-context.service';

@Injectable({
  providedIn: 'root',
})
export class ResourceCommandStream extends CommandStream<ResourceCommand> {
  private saveErrorService = inject(SaveErrorService);
  private maintenanceMode = inject(MaintenanceModeService);

  private defaultDebounceTimeInMilliseconds = 400;

  private rebuildBufferDelayAbortController: AbortController | undefined;

  protected async rebuildBuffer(): Promise<Array<ResourceCommand>> {
    // Getting the buffered commands reaches the get property, which will move any queued buffered commands
    // into the buffered commands.

    let bufferedCommands = this.bufferedCommands;
    let previousSequentialPatchCommandsCount = 0;

    let sequentialPatchCommands = this.getSequentialPatchCommands(bufferedCommands);
    let sequentialPatchCommandsCount = sequentialPatchCommands.length;

    // If the sequentialPatchCommandsCount doesn't match the previous count, than we're still adding
    // new patch commands and can continue to wait.
    let waitForNewPatchCommands = sequentialPatchCommandsCount != previousSequentialPatchCommandsCount;

    // Continue to wait the debounce amount while the sequentialPatchCommandsCount
    // grows. This can happen if patch commands for the same resource (same key) are
    // being added to the buffer while the delay is waiting.
    while (waitForNewPatchCommands) {
      this.rebuildBufferDelayAbortController ??= new AbortController();
      const abortSignal = this.rebuildBufferDelayAbortController.signal;

      try {
        await delayAsync(this.defaultDebounceTimeInMilliseconds, abortSignal);
      } catch {
        /* Do nothing! */
      }

      if (abortSignal.aborted) {
        this.rebuildBufferDelayAbortController = new AbortController();
      }

      // Get an updated set of the buffered commands since they may have changed during the delay
      bufferedCommands = this.bufferedCommands;

      previousSequentialPatchCommandsCount = sequentialPatchCommandsCount;
      sequentialPatchCommands = this.getSequentialPatchCommands(bufferedCommands);
      sequentialPatchCommandsCount = sequentialPatchCommands.length;
      waitForNewPatchCommands = sequentialPatchCommandsCount != previousSequentialPatchCommandsCount;
    }

    // Remove the rebuildBufferDelayAbortController since we're done processing, and we don't want
    // calls to "onCommandAdded" to trigger the abort before the next loop of the rebuild starts.
    delete this.rebuildBufferDelayAbortController;

    // Collapse the sequentialPatchCommands if more than 1 is found
    if (sequentialPatchCommandsCount > 1) {
      const collapsedPatchCommand = sequentialPatchCommands[0];
      for (let i = 1; i < sequentialPatchCommands.length; i++) {
        const mergeCommand = sequentialPatchCommands[i];
        collapsedPatchCommand.merge(mergeCommand);
      }
      // Remove resources that have been merged down
      bufferedCommands.splice(1, sequentialPatchCommandsCount - 1);
    }

    // Returns the rebuilt bufferedCommands
    return bufferedCommands;
  }

  private getSequentialPatchCommands(bufferedCommands: Array<ResourceCommand>): Array<PatchResourceCommand<Resource>> {
    const sequentialPatchCommands: Array<PatchResourceCommand<Resource>> = [];
    let previousCommandKey = '';
    for (const command of bufferedCommands) {
      const isPatchCommand = command instanceof PatchResourceCommand;
      if (!isPatchCommand) break;

      const patchCommand = command as PatchResourceCommand<Resource>;

      const currentCommandKey = patchCommand.key;
      previousCommandKey ||= patchCommand.key;
      if (currentCommandKey != previousCommandKey) break;

      sequentialPatchCommands.push(patchCommand);
    }
    return sequentialPatchCommands;
  }

  protected override onCommandAdded() {
    this.rebuildBufferDelayAbortController?.abort('Cancelled');
  }

  protected override async executeCommand(command: Command): Promise<void> {
    const asyncWorker = new AsyncWorker()
      .withTimeout(
        1,

        () => (this.saveErrorService.state = SaveErrorStates.slow),
      )
      .onError(() => {
        if (this.maintenanceMode.isActive) return;

        this.saveErrorService.state = SaveErrorStates.failed;
      })
      .onSuccess(() => {
        // If we were slow, and there are commands remaining, don't clear the slow state.
        const isSlow = this.saveErrorService.state == SaveErrorStates.slow;
        const hasCommands = this.commandsCount > 0;
        if (isSlow && hasCommands) return;

        this.saveErrorService.state = SaveErrorStates.noError;
      });

    await asyncWorker.start(async () => {
      // await delayAsync(1000); // Testing. Uncomment to test slow state.
      return await command.execute();
    });
  }
}

export interface ResourceCommand extends Command {}

export abstract class ResourceCommandBase extends CommandBase implements ResourceCommand {
  constructor() {
    super();
  }
}

export class PostResourceCommand<T extends Resource> extends ResourceCommandBase {
  constructor(
    private parentResource: Resource,
    private relationName: string,
    public readonly resource?: Partial<T>,
  ) {
    super();
  }

  protected async doWork(): Promise<any> {
    const postResource$ = this.parentResource.postRelation(this.relationName, {
      body: this.resource,
    });

    return await lastValueFrom(postResource$);
  }
}

export class PatchResourceCommand<T extends Resource> extends ResourceCommandBase {
  get key(): string {
    return ResourceContext.getResourceKey(this.resource);
  }

  constructor(
    public readonly resource: T,
    public snapshot: Partial<T>,
    private hateoasResourceService: HateoasResourceService,
  ) {
    super();
  }

  protected override async doWork(): Promise<any> {
    //bail if the snapshot is empty
    if (this.snapshot && Object.keys(this.snapshot).length === 0) return;

    const patchResource$ = this.hateoasResourceService.patchResource(this.resource, {
      body: this.snapshot,
      valuesOption: {
        include: Include.NULL_VALUES,
      },
    });

    return await lastValueFrom(patchResource$);
  }

  merge(command: PatchResourceCommand<T>, sync = true) {
    if (command.key != this.key) throw 'Cannot merge commands with mismatched keys';

    Object.assign(this.snapshot, command.snapshot);

    // Merged commands will be removed from the buffer,
    // but it's still possibly useful to know when the
    // work is completed. Syncing the result will make
    // its result promise resolve when the command handling
    // the work resolves.
    if (sync) command.sync(this);
  }
}

export class DeleteResourceCommand<T extends Resource> extends ResourceCommandBase {
  constructor(
    public readonly resource: T,
    private hateoasResourceService: HateoasResourceService,
    private params: any = null,
  ) {
    super();
  }

  protected override async doWork(): Promise<any> {
    const deleteResource$ = this.hateoasResourceService.deleteResource(this.resource, { params: this.params });
    return await lastValueFrom(deleteResource$);
  }
}
